import {
  takeLatest,
  call,
  put,
  select,
  CallEffect,
  PutEffect,
  SelectEffect,
  ForkEffect,
} from 'redux-saga/effects';
import { PayloadAction, AnyAction } from '@reduxjs/toolkit';

import { buildApiUrl } from 'environment';
import fetchHelper, { isOkHttpResponse } from 'services/apiService';
import type { HttpResponse } from 'services/apiService';
import {
  createErrorId,
  createBackendErrorMessage,
  createNetworkErrorMessage,
} from 'services/errorService';
import createStatusCounts from 'state/shared/createStatusCounts';

import type { CountryCodesResponseBody } from '../shared/apiTypes';
import type { CountryCodes } from './countryCodesSlice';
import { countryCodesActions } from './countryCodesSlice';
import { selectCountryCodes } from './countryCodesSelectors';

const { COUNTRY_CODES_REQUEST, COUNTRY_CODES_SUCCESS, COUNTRY_CODES_FAILURE } =
  countryCodesActions;

const statusCounts = createStatusCounts(countryCodesActions);

function* getCountryCodes(
  _action: PayloadAction
): Generator<
  CallEffect<AnyAction> | PutEffect<AnyAction> | SelectEffect,
  void,
  HttpResponse<CountryCodesResponseBody> & CountryCodes
> {
  const { type: errorActionType } = COUNTRY_CODES_FAILURE;
  const existingCountryCodes = yield select(selectCountryCodes);
  if (!existingCountryCodes.length) {
    try {
      const response = yield call(
        fetchHelper.get,
        buildApiUrl('countryCodes') // TODO: change this to enum
      );
      if (
        isOkHttpResponse<typeof response, CountryCodesResponseBody>(
          response,
          response.parsedBody
        )
      ) {
        const countryCodes: CountryCodes = Object.entries(response.parsedBody)
          .map(([code, country]) => ({ code: parseInt(code, 10), country }))
          .sort(({ country: country1Name }, { country: country2Name }) =>
            country1Name.localeCompare(country2Name)
          );
        yield put(COUNTRY_CODES_SUCCESS(countryCodes));
      } else if (response) {
        // backend error
        yield put(
          COUNTRY_CODES_FAILURE({
            id: createErrorId(
              errorActionType,
              (statusCounts[errorActionType] += 1)
            ),
            error: createBackendErrorMessage(response),
          })
        );
      }
    } catch (error) {
      // network error
      if (error instanceof Error) {
        yield put(
          COUNTRY_CODES_FAILURE({
            id: createErrorId(
              errorActionType,
              (statusCounts[errorActionType] += 1)
            ),
            error: createNetworkErrorMessage(error),
          })
        );
      }
    }
  } else {
    yield put(COUNTRY_CODES_SUCCESS(existingCountryCodes));
  }
}

export default function* countryCodesSaga(): Generator<
  ForkEffect<never>,
  void,
  unknown
> {
  yield takeLatest(COUNTRY_CODES_REQUEST, getCountryCodes);
}
